/**
 * Modal para mostrar el reporte detallado de archivos de participantes.
 *
 * Este componente proporciona una interfaz para visualizar estadísticas detalladas
 * de los archivos de carga masiva, incluyendo porcentajes de éxito, errores y
 * información del procesamiento.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-12]
 * @updated [Fecha de última modificación: 2025-08-12]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import {
  Box,
  Divider,
  Icon,
  Modal,
  Typography,
  Card,
  Grid,
  LinearProgress,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";

export function ParticipantFilesReport({ open, handleClose, uploadData }) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "600px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    maxHeight: "90vh",
    overflow: "auto",
  };

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Fecha inválida"
      : date.toISOString().slice(0, 19).replace("T", " ");
  };

  /**
   * Calcula el porcentaje de éxito
   * @param {number} successful - Filas exitosas
   * @param {number} total - Total de filas
   * @returns {number} Porcentaje de éxito
   */
  const calculateSuccessRate = (successful, total) => {
    if (total === 0) return 0;
    return Math.round((successful / total) * 100);
  };

  /**
   * Calcula el porcentaje de errores
   * @param {number} errors - Filas con errores
   * @param {number} total - Total de filas
   * @returns {number} Porcentaje de errores
   */
  const calculateErrorRate = (errors, total) => {
    if (total === 0) return 0;
    return Math.round((errors / total) * 100);
  };

  /**
   * Obtiene el color del estado
   * @param {string} status - Estado del procesamiento
   * @returns {string} Color del estado
   */
  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "PARTIAL":
        return "warning";
      case "PROCESSING":
        return "info";
      default:
        return "default";
    }
  };

  /**
   * Obtiene el texto del estado en español
   * @param {string} status - Estado del procesamiento
   * @returns {string} Texto del estado
   */
  const getStatusText = (status) => {
    switch (status) {
      case "COMPLETED":
        return "Completado";
      case "FAILED":
        return "Fallido";
      case "PARTIAL":
        return "Parcialmente exitoso";
      case "PROCESSING":
        return "Procesando";
      default:
        return status;
    }
  };

  if (!uploadData) return null;

  const successRate = calculateSuccessRate(uploadData.successful_rows, uploadData.total_rows);
  const errorRate = calculateErrorRate(uploadData.error_rows, uploadData.total_rows);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography id="modal-modal-title" component="h2" variant="h5">
            Reporte de Carga Masiva
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
            }}
            onClick={handleClose}>
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />

        <Box sx={{ mt: 2 }}>
          {/* Información del archivo */}
          <Typography variant="h6" gutterBottom>
            Información del Archivo
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Nombre:</strong> {uploadData.original_filename}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Estado:</strong>{" "}
                <Chip
                  label={getStatusText(uploadData.status)}
                  color={getStatusColor(uploadData.status)}
                  size="small"
                />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Tamaño:</strong> {Math.round(uploadData.file_size / 1024)} KB
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Fecha de carga:</strong> {formatDate(uploadData.created)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Fecha de procesamiento:</strong> {formatDate(uploadData.processed_at)}
              </Typography>
            </Grid>
          </Grid>

          {/* Estadísticas de procesamiento */}
          <Typography variant="h6" gutterBottom>
            Estadísticas de Procesamiento
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Total de filas:</strong> {uploadData.total_rows}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Filas omitidas:</strong> {uploadData.skipped_rows}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" style={{ color: "green" }}>
                <strong>Filas exitosas:</strong> {uploadData.successful_rows}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" style={{ color: "red" }}>
                <strong>Filas con errores:</strong> {uploadData.error_rows}
              </Typography>
            </Grid>
          </Grid>

          {/* Porcentajes con barras de progreso */}
          <Typography variant="h6" gutterBottom>
            Porcentajes de Procesamiento
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Tasa de éxito</Typography>
              <Typography variant="body2">{successRate}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={successRate}
              color="success"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Tasa de errores</Typography>
              <Typography variant="body2">{errorRate}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={errorRate}
              color="error"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Box>

        <Divider variant="middle" />
        <MDBox
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}>
          <MDButton color="info" onClick={handleClose}>
            Cerrar
          </MDButton>
        </MDBox>
      </Card>
    </Modal>
  );
}

ParticipantFilesReport.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  uploadData: PropTypes.object,
};
